import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from './Box';

interface ExpandableTextProps {
  children: React.ReactNode;
  showMore?: boolean;
  lines?: number;
  sx?: SxProps;
}

export const ExpandableText: React.FC<ExpandableTextProps> = ({
  children,
  showMore = false,
  lines = 3,
  sx,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isContentOverflowing, setIsContentOverflowing] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null); // Correctly typed ref for a div element
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        setIsContentOverflowing(
          contentRef.current.scrollHeight > contentRef.current.clientHeight
        );
      }
    };

    checkOverflow();
  }, []);

  const lineClampedStyle = [
    {
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      WebkitLineClamp: !expanded ? lines : 'unset', // Remove clamp when expanded by setting it to undefined
      cursor: isContentOverflowing ? 'pointer' : 'default', // Change cursor on hover when not expanded
    },
  ];

  return (
    <Box sx={sx}>
      <Box
        ref={contentRef}
        component="div"
        sx={lineClampedStyle}
        onClick={isContentOverflowing ? toggleExpanded : undefined} // Enable click only when not expanded
      >
        {children}
      </Box>
      {(expanded || showMore) && isContentOverflowing && (
        <Typography variant="body2">
          <Link component="button" onClick={toggleExpanded} sx={{ mt: 1 }}>
            {expanded ? t('show less') : t('show more')}
          </Link>
        </Typography>
      )}
    </Box>
  );
};
